
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowUpRight,
  CalendarDays,
  ChevronRight,
  FileText,
  Loader2,
  Newspaper,
  RefreshCw,
  Sparkles,
} from "lucide-react";

import { api, resolveMediaUrl } from "../lib/api";

/* ==========================================
   HELPERS
========================================== */

function formatNewsDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getNewsItems(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
}

function getNewsImage(item) {
  const possibleImages = [
    item?.image?.url,
    item?.image?.path,
    item?.imageUrl,
    item?.image,
  ];

  const image = possibleImages.find(
    (value) =>
      typeof value === "string" &&
      value.trim() !== ""
  );

  return image ? resolveMediaUrl(image) : "";
}

/* ==========================================
   NEWS CARD
========================================== */

function NewsCard({ item, index }) {
  const imageUrl = getNewsImage(item);
  const formattedDate = formatNewsDate(item.date);

  const category = item.type || "School Update";

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{
        duration: 0.45,
        delay: Math.min(index * 0.06, 0.18),
      }}
      className="group flex h-full flex-col overflow-hidden rounded-[22px] border border-red-100/80 bg-white shadow-[0_10px_35px_rgba(80,30,30,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-red-200 hover:shadow-[0_18px_45px_rgba(80,30,30,0.12)]"
    >
      {/* CLEAN IMAGE */}
      {imageUrl ? (
        <div className="relative overflow-hidden bg-slate-100">
          <div className="h-[215px] overflow-hidden sm:h-[230px]">
            <img
              src={imageUrl}
              alt={item.image?.alt || item.title || "School news"}
              loading="lazy"
              className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          </div>

          {/* Decorative bottom line */}
          <div className="h-[3px] w-full bg-gradient-to-r from-red-800 via-red-500 to-amber-400" />
        </div>
      ) : (
        <div className="relative flex h-[160px] items-center justify-center overflow-hidden bg-gradient-to-br from-red-50 via-white to-amber-50">
          <div className="absolute -right-7 -top-7 h-28 w-28 rounded-full border-[14px] border-red-100/60" />

          <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-red-100 bg-white text-red-800 shadow-sm">
            <Newspaper size={30} strokeWidth={1.5} />
          </div>

          <div className="absolute bottom-0 left-0 h-[3px] w-full bg-gradient-to-r from-red-800 to-amber-400" />
        </div>
      )}

      {/* CARD BODY */}
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        {/* CATEGORY AND DATE */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center rounded-full border border-red-100 bg-red-50 px-3 py-1.5 text-[11px] font-medium text-red-800">
            {category}
          </span>

          {formattedDate && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-normal text-slate-500">
              <CalendarDays
                size={14}
                className="text-amber-600"
              />
              {formattedDate}
            </span>
          )}
        </div>

        {/* TITLE */}
        <h2 className="mt-4 font-serif text-[19px] font-medium leading-[1.45] text-slate-900 transition-colors duration-300 group-hover:text-red-800 sm:text-[21px]">
          {item.title || "School Announcement"}
        </h2>

        {/* DESCRIPTION */}
        {item.description && (
          <p className="mt-3 flex-1 whitespace-pre-line text-[13px] font-normal leading-[1.85] text-slate-600 sm:text-[14px]">
            {item.description}
          </p>
        )}

        {/* CARD FOOTER */}
        <div className="mt-auto pt-5">
          <div className="mb-4 h-px w-full bg-gradient-to-r from-red-100 via-slate-100 to-transparent" />

          {item.link ? (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[13px] font-medium text-red-800 transition-all duration-300 hover:gap-3 hover:text-red-600"
            >
              Read Full Update

              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-50 transition-colors group-hover:bg-red-800 group-hover:text-white">
                <ArrowUpRight size={15} />
              </span>
            </a>
          ) : (
            <div className="flex items-center gap-2 text-xs font-normal text-slate-400">
              <FileText size={14} />
              School News & Announcements
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}

/* ==========================================
   MAIN COMPONENT
========================================== */

export default function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;

    async function loadNews() {
      setLoading(true);
      setError("");

      try {
        const response = await api.activeNews();

        if (!active) return;

        const records = getNewsItems(response).filter(
          (item) => item.isActive !== false
        );

        setItems(records);
      } catch (err) {
        if (!active) return;

        setError(
          err?.message ||
            "Unable to load school news and announcements."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadNews();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  // Display the latest news first.
  const newsItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const firstDate = new Date(
        a.date || a.createdAt || 0
      ).getTime();

      const secondDate = new Date(
        b.date || b.createdAt || 0
      ).getTime();

      return (
        (Number.isNaN(secondDate) ? 0 : secondDate) -
        (Number.isNaN(firstDate) ? 0 : firstDate)
      );
    });
  }, [items]);

  return (
    <section className="relative isolate min-h-screen w-full overflow-hidden bg-[#FFFCF9] pb-16">
      {/* BACKGROUND DECORATION */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 top-48 h-96 w-96 rounded-full bg-red-100/60 blur-3xl" />

        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-amber-100/60 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.022]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "27px 27px",
          }}
        />
      </div>

      {/* HERO HEADER */}
      <div className="relative overflow-hidden bg-gradient-to-r from-red-900 via-red-800 to-red-700 px-4 pb-20 pt-12 text-white sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[36px] border-white/5" />

        <div className="pointer-events-none absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-amber-300/10 blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mx-auto max-w-7xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/10 px-4 py-2 text-[11px] font-normal tracking-wide text-amber-100">
            <Sparkles size={15} />
            School Updates
          </span>

          <h1 className="mt-5 font-serif text-[32px] font-medium leading-tight sm:text-[42px] lg:text-[50px]">
            News &{" "}
            <span className="text-amber-300">
              Announcements
            </span>
          </h1>

          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-amber-400" />

          <p className="mx-auto mt-5 max-w-2xl text-[13px] font-normal leading-7 text-white/85 sm:text-[15px]">
            Discover the latest school news, important
            announcements, academic updates, events,
            and memorable moments from our campus.
          </p>
        </motion.div>
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-10 mx-auto -mt-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* SUBHEADING */}
        <div className="mb-7 flex flex-col gap-3 rounded-2xl border border-red-100 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
              <Newspaper size={20} />
            </div>

            <div>
              <h2 className="font-serif text-lg font-medium text-slate-900">
                Latest School Updates
              </h2>

              <p className="mt-0.5 text-xs font-normal text-slate-500">
                Stay informed about school activities.
              </p>
            </div>
          </div>

          {!loading && !error && (
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-normal text-amber-800">
              <Newspaper size={14} />
              {newsItems.length}{" "}
              {newsItems.length === 1
                ? "Update"
                : "Updates"}
            </span>
          )}
        </div>

        {/* LOADING */}
        {loading ? (
          <div className="flex min-h-[350px] flex-col items-center justify-center rounded-2xl border border-red-100 bg-white shadow-sm">
            <Loader2
              size={32}
              className="animate-spin text-red-800"
            />

            <p className="mt-4 text-sm font-normal text-slate-600">
              Loading school updates...
            </p>
          </div>
        ) : error ? (
          /* ERROR */
          <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white px-6 py-10 text-center shadow-sm">
            <AlertCircle
              size={34}
              className="mx-auto text-red-700"
            />

            <h2 className="mt-3 text-lg font-medium text-slate-900">
              Unable to Load Updates
            </h2>

            <p className="mt-2 text-sm text-red-700">
              {error}
            </p>

            <button
              type="button"
              onClick={() =>
                setRefreshKey((key) => key + 1)
              }
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-2.5 text-sm font-normal text-white transition hover:bg-red-900"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : newsItems.length === 0 ? (
          /* EMPTY STATE */
          <div className="rounded-2xl border border-red-100 bg-white px-6 py-16 text-center shadow-sm">
            <Newspaper
              size={43}
              className="mx-auto text-red-200"
            />

            <h2 className="mt-4 font-serif text-xl font-medium text-slate-900">
              No Announcements Available
            </h2>

            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
              There are no news or announcements
              available at the moment. Please check
              back later for school updates.
            </p>
          </div>
        ) : (
          /* NEWS GRID */
          <div className="grid items-stretch gap-6 md:grid-cols-2 lg:grid-cols-3">
            {newsItems.map((item, index) => (
              <NewsCard
                key={item._id || `${item.title}-${index}`}
                item={item}
                index={index}
              />
            ))}
          </div>
        )}

        {/* BOTTOM INFORMATION */}
        {!loading && !error && newsItems.length > 0 && (
          <div className="mt-8 flex items-center justify-center gap-2 text-center text-xs font-normal text-slate-500">
            <ChevronRight size={15} className="text-red-700" />
            Stay connected for more school news and announcements.
          </div>
        )}
      </div>
    </section>
  );
}
